/**
 * Cliente HTTP da API.
 *
 * Componente nunca chama axios direto e store nunca monta URL na mão — ver
 * docs/AI_DIRETIVES/COMPONENTS.md § 6.
 */
import axios from 'axios';

// Caminho relativo: o nginx de produção e o dev-server já fazem proxy de /api.
// A URL absoluta que existia aqui mandava o navegador do visitante chamar a máquina
// dele mesmo em produção.
const baseURL = process.env.VUE_APP_API_BASE_URL || '/api';

const DEVICE_ID_KEY = 'sentido:device-id';
const TOKEN_KEY = 'sentido:token';

const http = axios.create({ baseURL });

http.interceptors.request.use((config) => {
  // As duas identidades convivem porque o anônimo é a experiência padrão: o token vence
  // quando existe, e o device_id sustenta quem ainda não criou conta.
  const deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (deviceId) config.headers['X-Device-Id'] = deviceId;

  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

/**
 * Identidade que o servidor não reconhece mais se refaz sozinha.
 *
 * `device_id` e token são cache de linhas do servidor. Quando elas somem — banco de
 * desenvolvimento recriado, ambiente novo, conta apagada, token expirado — toda chamada
 * volta 401 e a tela ficava em "Não foi possível iniciar o jogo", sem caminho de volta:
 * recarregar mandava a mesma credencial morta. Aqui as duas são descartadas, um jogador
 * anônimo é criado e a chamada refeita uma única vez.
 */
let refazendoJogador = null;

http.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  // Login e cadastro respondem 401 quando a senha está errada: ali o 401 é resposta, não
  // identidade podre, e refazer o jogador apagaria a tentativa da pessoa.
  const ehAutenticacao = (config?.url || '').startsWith('/auth/');
  // **Só** `not_authenticated` — "esta identidade não vale mais". `account_required` é
  // outra coisa: anônimo pedindo algo que pede conta. Tratar os dois igual trocava o
  // jogador por um novo assim que a aba de duelos pedia a lista de amigos, e o progresso
  // ia junto.
  const identidadePodre = error.response?.status === 401
    && error.response?.data?.error === 'not_authenticated'
    && (localStorage.getItem(DEVICE_ID_KEY) || localStorage.getItem(TOKEN_KEY));

  // `_refeita` impede o laço: se a segunda tentativa também falhar, o erro sobe.
  if (!identidadePodre || ehAutenticacao || !config || config._refeita) return Promise.reject(error);

  config._refeita = true;
  // O token também cai: sessão que o servidor recusa não fica guardada esperando a
  // próxima tela dar o mesmo 401.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(DEVICE_ID_KEY);
  // Uma criação só, mesmo com várias chamadas falhando juntas na abertura da tela.
  refazendoJogador = refazendoJogador || http.post('/auth/anonymous').then(({ data }) => {
    localStorage.setItem(DEVICE_ID_KEY, data.device_id);
    refazendoJogador = null;
    return data.device_id;
  }).catch((falha) => {
    refazendoJogador = null;
    throw falha;
  });

  await refazendoJogador;
  return http(config);
});

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/**
 * Garante que existe um jogador anônimo.
 *
 * O device_id é emitido e guardado pelo servidor; aqui ele é só cache. Um id gerado no
 * navegador seria um id que o jogador troca quando o placar não agrada.
 */
export async function ensurePlayer() {
  const existente = localStorage.getItem(DEVICE_ID_KEY);
  if (existente) return existente;

  const { data } = await http.post('/auth/anonymous');
  localStorage.setItem(DEVICE_ID_KEY, data.device_id);
  return data.device_id;
}

export async function fetchModes() {
  const { data } = await http.get('/games/modes');
  return data;
}

export async function fetchPlayerCard(playerId) {
  const { data } = await http.get(`/players/${playerId}/card`);
  return data;
}

export async function startGame(mode = 'classic-daily') {
  await ensurePlayer();
  const { data } = await http.post('/games', { mode });
  return data;
}

/** Como está a diária de cada modo hoje, sem abrir partida nenhuma. */
export async function fetchToday() {
  await ensurePlayer();
  const { data } = await http.get('/games/today');
  return data;
}

export async function resumeGame(gameId) {
  const { data } = await http.get(`/games/${gameId}`);
  return data;
}

export async function submitGuess(gameId, guess) {
  const { data } = await http.post(`/games/${gameId}/guess`, { guess });
  return data;
}

// ── duelo ────────────────────────────────────────────────────────────────────
export async function fetchDuels() {
  await ensurePlayer();
  const { data } = await http.get('/duels');
  return data;
}

export async function fetchDuel(id) {
  const { data } = await http.get(`/duels/${id}`);
  return data;
}

export async function createDuel(payload) {
  await ensurePlayer();
  const { data } = await http.post('/duels', payload);
  return data;
}

export async function acceptDuel(id) {
  const { data } = await http.post(`/duels/${id}/accept`);
  return data;
}

export async function declineDuel(id) {
  const { data } = await http.post(`/duels/${id}/decline`);
  return data;
}

export async function joinDuel(code) {
  await ensurePlayer();
  const { data } = await http.post(`/duels/join/${code}`);
  return data;
}

export async function startDuelRoom(id) {
  const { data } = await http.post(`/duels/${id}/start`);
  return data;
}

export async function playDuel(id) {
  const { data } = await http.post(`/duels/${id}/play`);
  return data;
}

export async function nextDuelRound(id) {
  const { data } = await http.post(`/duels/${id}/rounds`);
  return data;
}

export async function removeDuel(id) {
  await http.delete(`/duels/${id}`);
}

export async function forfeitDuel(id) {
  const { data } = await http.post(`/duels/${id}/forfeit`);
  return data;
}

// ── social ───────────────────────────────────────────────────────────────────
export async function fetchRelationship(playerId) {
  const { data } = await http.get(`/players/${playerId}/relationship`);
  return data;
}

export async function followPlayer(playerId) {
  const { data } = await http.post(`/players/${playerId}/follow`);
  return data;
}

export async function unfollowPlayer(playerId) {
  const { data } = await http.delete(`/players/${playerId}/follow`);
  return data;
}

export async function requestFriendship(playerId) {
  const { data } = await http.post(`/players/${playerId}/friend`);
  return data;
}

export async function acceptFriendship(playerId) {
  const { data } = await http.post(`/players/${playerId}/friend/accept`);
  return data;
}

export async function dropFriendship(playerId) {
  const { data } = await http.delete(`/players/${playerId}/friend`);
  return data;
}

export async function blockPlayer(playerId) {
  const { data } = await http.post(`/players/${playerId}/block`);
  return data;
}

export async function unblockPlayer(playerId) {
  const { data } = await http.delete(`/players/${playerId}/block`);
  return data;
}

export async function reportPlayer(playerId, reason, detail = null) {
  await http.post(`/players/${playerId}/report`, { reason, detail });
}

export async function fetchFriends() {
  const { data } = await http.get('/friends');
  return data;
}

export async function fetchFriendRequests() {
  const { data } = await http.get('/friends/requests');
  return data;
}

export async function createInvite(kind = 'single') {
  const { data } = await http.post('/friends/invites', { kind });
  return data;
}

export async function redeemInvite(code) {
  const { data } = await http.post(`/friends/invites/${code}`);
  return data;
}

export async function fetchPhaseFriends(number) {
  const { data } = await http.get(`/trail/phases/${number}/friends`);
  return data;
}

export async function fetchTrail() {
  await ensurePlayer();
  const { data } = await http.get('/trail');
  return data;
}

/** Abre a partida de uma fase. Fase travada ou sem energia é recusa do servidor. */
export async function startPhase(number) {
  await ensurePlayer();
  const { data } = await http.post(`/trail/phases/${number}`);
  return data;
}

/** Desiste da partida aberta. Vale para diária, treino, fase da trilha e duelo. */
export async function forfeitGame(gameId) {
  const { data } = await http.post(`/games/${gameId}/forfeit`);
  return data;
}

/** O estado da partida como o servidor o vê — usado quando a tela sai de sincronia. */
export async function fetchGame(gameId) {
  const { data } = await http.get(`/games/${gameId}`);
  return data;
}

export async function getSummary(gameId) {
  const { data } = await http.get(`/games/${gameId}/summary`);
  return data;
}

export default http;

export async function register(payload) {
  const { data } = await http.post('/auth/register', payload);
  setToken(data.token);
  return data;
}

export async function login(payload) {
  const { data } = await http.post('/auth/login', payload);
  setToken(data.token);
  return data;
}

export async function claim(payload) {
  const { data } = await http.post('/auth/claim', payload);
  setToken(data.token);
  return data;
}

/**
 * Troca a senha. A troca derruba toda sessão aberta, inclusive esta: o token novo vem na
 * resposta e substitui o antigo aqui mesmo.
 */
export async function changePassword(payload) {
  const { data } = await http.post('/me/password', payload);
  setToken(data.token);
  return data;
}

/** Pede o link de redefinição. A resposta é a mesma com ou sem conta para o e-mail. */
export async function requestPasswordReset(email) {
  await http.post('/auth/password-reset/request', { email });
}

/** Troca a senha pelo token do link, e já abre a sessão com o token que volta. */
export async function confirmPasswordReset(payload) {
  const { data } = await http.post('/auth/password-reset/confirm', payload);
  setToken(data.token);
  return data;
}

export function logout() {
  setToken(null);
}

export async function fetchMe() {
  const { data } = await http.get('/me');
  return data;
}

export async function fetchSeason() {
  const { data } = await http.get('/seasons/current');
  return data;
}

export async function fetchSeasonHistory() {
  const { data } = await http.get('/seasons');
  return data;
}

export async function fetchSeasonStandings(number) {
  const { data } = await http.get(`/seasons/${number}/standings`);
  return data;
}

export async function fetchRanking(scope = 'global', period = 'season') {
  const { data } = await http.get('/rankings', { params: { scope, period } });
  return data;
}

export async function exportData() {
  const { data } = await http.get('/me/export');
  return data;
}

export async function deleteAccount() {
  await http.delete('/me');
  setToken(null);
}
